import React, { useEffect } from 'react'
import config from 'cityads-v3/config'
import { useTranslation, Trans } from 'react-i18next'
import { useRegisterAdvertiserForm, useSearchForm } from './RegisterAdvertiserForm.hooks'
import { getPrivacyPolicy } from 'cityads-v3/components/Footer/Footer'
import classes from './RegisterAdvertiserForm.scss'

import Checkbox from '@material-ui/core/Checkbox'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import Recaptcha from 'react-recaptcha'
import SelectField from 'cityads-v3/components/TextField/SelectField'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'

type RegisterAdvertiserFormProps = {
  onMount?: () => void
}

export default function RegisterAdvertiserForm({ onMount }: RegisterAdvertiserFormProps): JSX.Element {
  const { t } = useTranslation(['main'])
  const {
    form,
    handleSendForm,
    handleRecaptchaCallback,
    countries,
    industries,
    language,
  } = useRegisterAdvertiserForm()

  const searchForm = useSearchForm()

  useEffect(() => {
    onMount?.();
  }, [onMount])

  return (
    <div className={classes.RAF}>
      {form.isSent ?
        (
          <div className={classes.RAF__success}>
            <CheckCircleIcon color="primary" className={classes.successIcon} /><br />
            {t('registration.successLine1')}<br /><br />
            {t('registration.successLine2')}
          </div>
        )
        :
        (
          <>
            <div className={classes.formItems}>
              <TextField
                fullWidth
                label={t('registration.first_name')}
                error={form.fields.first_name.error}
                helperText={form.fields.first_name.errorText}
                onChange={form.fields.first_name.handleChange}
                value={form.fields.first_name.value}
              />

              <TextField
                fullWidth
                label={t('registration.last_name')}
                error={form.fields.last_name.error}
                helperText={form.fields.last_name.errorText}
                onChange={form.fields.last_name.handleChange}
                value={form.fields.last_name.value}
              />

              <TextField
                fullWidth
                label={t('registration.company')}
                error={form.fields.company.error}
                helperText={form.fields.company.errorText}
                onChange={form.fields.company.handleChange}
                value={form.fields.company.value}
              />

              <TextField
                fullWidth
                label={t('registration.phone')}
                error={form.fields.phone.error}
                helperText={form.fields.phone.errorText}
                onChange={form.fields.phone.handleChange}
                value={form.fields.phone.value}
              />

              <TextField
                fullWidth
                label={t('registration.email')}
                error={form.fields.email.error}
                helperText={form.fields.email.errorText}
                onChange={form.fields.email.handleChange}
                value={form.fields.email.value}
              />

              <TextField
                fullWidth
                label={t('registration.URL')}
                error={form.fields.url.error}
                helperText={form.fields.url.errorText}
                onChange={form.fields.url.handleChange}
                value={form.fields.url.value}
              />

              <SelectField<number, number>
                fullWidth
                label={t('registration.industry')}
                items={industries}
                error={form.fields.industry.error}
                helperText={form.fields.industry.errorText}
                handleChange={form.fields.industry.setValue}
                value={form.fields.industry.value}
                variant="standard"
              />

              <SelectField<number, number>
                fullWidth
                searchProps={{
                  frontFilter: true,
                  onChange: searchForm.fields.country.handleChange,
                  value: searchForm.fields.country.value
                }}
                label={t('registration.country')}
                items={countries}
                error={form.fields.country.error}
                helperText={form.fields.country.errorText}
                handleChange={form.fields.country.setValue}
                value={form.fields.country.value}
                variant="standard"
              />

              <TextField
                fullWidth
                label={t('registration.comment')}
                error={form.fields.comment.error}
                helperText={form.fields.comment.errorText}
                onChange={form.fields.comment.handleChange}
                value={form.fields.comment.value}
              />

              <FormControl
                fullWidth
                error={form.fields.agreement.error}
              >
                <FormControlLabel
                  label={
                    <Trans t={t} i18nKey="registration.agreement">
                      I accept the conditions of the <a href={getPrivacyPolicy(language)} target="_blank" rel="noreferrer">privacy policy</a> and consent to the processing and distribution of personal data.
                    </Trans>
                  }
                  control={<Checkbox
                    color="primary"
                    checked={form.fields.agreement.value}
                    onChange={form.fields.agreement.toggle}
                  />}
                />
                <FormHelperText error={form.fields.agreement.error}>{form.fields.agreement.errorText}</FormHelperText>
              </FormControl>

              <Recaptcha
                sitekey={config.RECAPTCHA_KEY}
                size="invisible"
                verifyCallback={handleRecaptchaCallback}
              />
              <WaitingButton
                type="submit"
                color="primary"
                fullWidth={true}
                variant="contained"
                waiting={form.isSending}
                onClick={handleSendForm}
              >
                {t('registration.submit')}
              </WaitingButton>

              {form.isSent && !form.isSuccess ? (
                <Typography color="error">
                  {t('forms.undefinedError')}
                </Typography>
              ) : null}
            </div>
          </>
        )}
    </div>
  )
}
